import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Bookmark } from 'lucide-react';
import useUserUuid from './useUserUuid';

/**
 * Compact "My saved trips (N)" launcher for the DEFAULT (non-compact) planner
 * card footer. Hidden entirely when the user has no saved trips, so it never
 * adds chrome for first-time visitors.
 */
const MyTripsLauncher = () => {
  const { myTrips } = useUserUuid();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (myTrips.length === 0) return null;

  return (
    <div className="relative" ref={wrapRef} data-testid="my-trips-launcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-wine-700 hover:text-wine-900 transition-colors"
        data-testid="my-trips-launcher-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Bookmark className="w-3 h-3" />
        My saved trips ({myTrips.length})
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 bottom-full mb-2 w-64 max-h-72 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 z-30"
          role="listbox"
          data-testid="my-trips-launcher-dropdown"
        >
          {myTrips.map((t) => (
            <Link
              key={t.slug}
              to={`/trip/${t.slug}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 hover:bg-gold-50 transition-colors"
              data-testid={`my-trips-launcher-item-${t.slug}`}
            >
              <div className="text-xs text-[#1a1a2e] font-medium truncate" title={t.query}>
                {t.query || 'Untitled trip'}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">
                /trip/{t.slug}
                {t.created_at &&
                  ` · ${new Date(t.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTripsLauncher;
